import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Paper, Typography, TextField, IconButton, CircularProgress, Link, Stack, Alert, Tooltip } from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate } from 'react-router-dom';
import { useOrganization } from '../../../context/OrganizationContext';
import { useViewRender } from '../../../context/ViewRenderContext';
import { useChatStream } from '../../../hooks/useChatStream';
import { toApiScope } from '../../../services/chat/chat-scope';
import { makeChatStorageKey } from '../../../services/chat/chat-storage';
import { ChatRequestBody, ChatHistoryMessage, ChatCitation } from '../../../services/chat/chat-sse';

interface ChatRendererProps {
  componentId?: string;
  isEditing?: boolean;
  isSelected?: boolean;
  scopeBlockIds?: string[];
  title?: string;
  placeholder?: string;
  height?: number;
  showCitations?: boolean;
  onUpdate?: (props: any) => void;
}

const ChatRenderer: React.FC<ChatRendererProps> = ({
  componentId,
  scopeBlockIds,
  title = 'Ask about this content',
  placeholder = 'Ask a question…',
  height = 420,
  showCitations = true,
}) => {
  const navigate = useNavigate();
  const { currentOrganizationId, currentTeamId } = useOrganization();
  const { viewId, pageDoc } = useViewRender();
  const storageKey = useMemo(
    () => makeChatStorageKey({ viewId, componentId }),
    [viewId, componentId],
  );
  const { turns, streaming, error, send, reset } = useChatStream({ storageKey });
  const [input, setInput] = useState('');
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const prevLenRef = useRef(0);

  // Auto-scroll: snap to the bottom when a new turn is added; while a response
  // streams in, follow it only if the user is already near the bottom so we
  // don't yank them down if they've scrolled up to read earlier messages.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const grew = turns.length > prevLenRef.current;
    prevLenRef.current = turns.length;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    if (grew || nearBottom) el.scrollTop = el.scrollHeight;
  }, [turns, streaming]);

  const availableBlockIds = useMemo(() => {
    const out: string[] = [];
    (pageDoc?.layout ?? []).forEach((s: any) =>
      s.rows?.forEach((r: any) =>
        r.columns?.forEach((c: any) =>
          c.blocks?.forEach((b: any) => {
            if (b.type === 'ViewContentQuery' || b.type === 'ContentQuery' ||
                b.type === 'ViewContentPick' || b.type === 'ContentPick') out.push(b.id);
          }),
        ),
      ),
    );
    return out;
  }, [pageDoc]);
  const scope = useMemo(() => toApiScope(scopeBlockIds, availableBlockIds, viewId), [scopeBlockIds, availableBlockIds, viewId]);
  const noContentBlocks = availableBlockIds.length === 0;
  const scopeUnset = !scope;

  const onSend = async () => {
    if (!scope || !currentOrganizationId || !currentTeamId) return;
    const question = input;
    setInput('');
    await send(question, (history: ChatHistoryMessage[]): ChatRequestBody => ({
      organization_id: currentOrganizationId,
      team_id: currentTeamId,
      scope,
      history,
    }));
  };

  return (
    <Paper variant="outlined" sx={{ display: 'flex', flexDirection: 'column', height }}>
      <Box
        sx={{
          px: 2, py: 1, borderBottom: '1px solid', borderColor: 'divider',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}
      >
        <Typography variant="subtitle2">{title}</Typography>
        {turns.length > 0 && (
          <Tooltip title="Clear conversation">
            <span>
              <IconButton size="small" onClick={reset} disabled={streaming} aria-label="Clear conversation">
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      <Box ref={scrollRef} sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
        {noContentBlocks ? (
          <Alert severity="info">Add a content query or pick block to this view to enable chat.</Alert>
        ) : scopeUnset ? (
          <Alert severity="info">Select at least one content block to chat about (in the settings panel).</Alert>
        ) : null}
        {turns.map((t, i) => (
          <Box key={i} sx={{ mb: 1.5, textAlign: t.role === 'user' ? 'right' : 'left' }}>
            <Typography
              variant="body2"
              sx={{
                display: 'inline-block', px: 1.5, py: 1, borderRadius: 2, whiteSpace: 'pre-wrap',
                bgcolor: t.role === 'user' ? 'primary.main' : 'grey.100',
                color: t.role === 'user' ? 'primary.contrastText' : 'text.primary',
              }}
            >
              {t.text || (streaming && t.role === 'assistant' ? '…' : '')}
            </Typography>
            {t.interrupted && <Typography variant="caption" color="warning.main"> (interrupted)</Typography>}
            {showCitations && t.role === 'assistant' && (t.citations?.length ?? 0) > 0 && (
              <Stack direction="row" spacing={1} sx={{ mt: 0.5, flexWrap: 'wrap', justifyContent: 'flex-start' }}>
                {t.citations!.map((c: ChatCitation) => (
                  <Link key={c.index} component="button" variant="caption" underline="hover"
                        onClick={() => navigate(`/content/${c.contentId}`)} title={citationLabel(c)}>
                    [{c.index}] {c.contentTitle}
                  </Link>
                ))}
              </Stack>
            )}
          </Box>
        ))}
        {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
      </Box>

      <Box sx={{ display: 'flex', gap: 1, p: 1, borderTop: '1px solid', borderColor: 'divider' }}>
        <TextField
          fullWidth size="small" placeholder={placeholder} value={input}
          disabled={streaming || scopeUnset}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); } }}
        />
        <IconButton color="primary" onClick={onSend} disabled={streaming || scopeUnset || !input.trim()}>
          {streaming ? <CircularProgress size={20} /> : <SendIcon />}
        </IconButton>
      </Box>
    </Paper>
  );
};

function citationLabel(c: ChatCitation): string {
  if (c.page != null) return `page ${c.page}`;
  if (c.slide != null) return `slide ${c.slide}`;
  if (c.startTime != null) return `at ${Math.round(c.startTime)}s`;
  if (c.sectionTitle) return c.sectionTitle;
  return c.chunkType;
}

export default ChatRenderer;
